/**
 * Custom hook holding the department list for the Departments tab.
 *
 * Mirrors `useAccessUsers`: fetch on mount, expose `loading` / `error` /
 * `retry`, and expose optimistic mutators that the caller pairs with its own
 * rollback call. This hook is a data container only — it issues no mutation
 * requests and holds no submission logic, so create / update / delete
 * orchestration stays in `DepartmentsTab` where the dialogs live (Req 2.2).
 *
 * One deliberate difference from `useAccessUsers`: the error string comes from
 * `formatErrorMessage(normalizeApiError(err))` rather than an ad-hoc
 * `err.response?.data?.message || err.message` chain, so the displayed text is
 * allow-listed and carries a reference id when the backend sends one
 * (Req 2.8, 12.4).
 *
 * Every mutator delegates to a pure reducer in `departmentState.js` and uses
 * the functional `setState` form, so concurrent optimistic updates compose
 * instead of overwriting each other — which is what makes the signed member
 * count deltas correct when two member mutations overlap.
 */

import { useState, useEffect, useCallback } from "react";
import { listDepartments, extractDepartments } from "./departmentsApi";
import {
  insertDepartmentAt as insertDepartmentAtReducer,
  replaceDepartment as replaceDepartmentReducer,
  removeDepartment as removeDepartmentReducer,
  applyMemberCountDelta,
} from "./departmentState";
import { normalizeApiError, formatErrorMessage } from "../../utils/apiErrors";

/**
 * @typedef {Object} Department
 * @property {number} id
 * @property {string} name
 * @property {string|null} description
 * @property {string[]} scopes
 * @property {boolean} is_active
 * @property {number} member_count
 * @property {string} created_at
 * @property {string|null} created_by
 */

/**
 * Hook for fetching and optimistically mutating the department list.
 *
 * @returns {{
 *   departments: Department[],
 *   loading: boolean,
 *   error: string|null,
 *   retry: () => void,
 *   insertDepartment: (department: Department) => void,
 *   insertDepartmentAt: (department: Department, index: number) => void,
 *   replaceDepartment: (id: number|string, patch: Object) => void,
 *   removeDepartment: (id: number|string) => void,
 *   adjustMemberCount: (id: number|string, delta: number) => void
 * }}
 */
export function useDepartments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listDepartments();
      setDepartments(extractDepartments(response.data));
    } catch (err) {
      setError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  /**
   * Inserts a department at a specific position. The delete rollback path uses
   * this with the index recorded before the removal, so a failed delete puts
   * the row back where it was rather than appending it (Req 5.5).
   */
  const insertDepartmentAt = useCallback((department, index) => {
    setDepartments((prev) =>
      insertDepartmentAtReducer(prev, department, index)
    );
  }, []);

  /**
   * Appends a department to the end of the list. Used by the create path,
   * which has no optimistic step because the id is only known once the server
   * responds (Req 3.6).
   */
  const insertDepartment = useCallback((department) => {
    setDepartments((prev) =>
      insertDepartmentAtReducer(prev, department, prev.length)
    );
  }, []);

  /**
   * Merges a patch into one department. The update path calls this twice: once
   * with the pending values, and again with the pre-submit snapshot when the
   * request fails (Req 4.3, 4.5).
   */
  const replaceDepartment = useCallback((id, patch) => {
    setDepartments((prev) => replaceDepartmentReducer(prev, id, patch));
  }, []);

  /**
   * Removes one department by id, for the optimistic delete step (Req 5.4).
   */
  const removeDepartment = useCallback((id) => {
    setDepartments((prev) => removeDepartmentReducer(prev, id));
  }, []);

  /**
   * Adjusts a department's member count by a signed delta. Deltas rather than
   * snapshots, so overlapping assign / remove requests for different emails
   * compose in any order and a rollback is just the inverse delta
   * (Req 6.8, 6.9, 6.11, 6.12).
   */
  const adjustMemberCount = useCallback((id, delta) => {
    setDepartments((prev) => applyMemberCountDelta(prev, id, delta));
  }, []);

  return {
    departments,
    loading,
    error,
    retry: fetchDepartments,
    insertDepartment,
    insertDepartmentAt,
    replaceDepartment,
    removeDepartment,
    adjustMemberCount,
  };
}

export default useDepartments;
